import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes and twMerge for Tailwind class conflicts
 * 
 * @param {...any} inputs - Class names or conditional class objects
 * @returns {string} Merged class string
 * 
 * @example
 * cn("px-2 py-1", "bg-red-500", { "text-white": isActive })
 * cn("px-2", "px-4") // Returns "px-4" (last one wins)
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Clean trailing zeros from user names
 * This function removes all trailing zeros from user names to fix issues like:
 * - "adminuser0" -> "adminuser"
 * - "Customer0" -> "Customer"
 * - "User000" -> "User"
 * 
 * @param {string|number|null|undefined} name - The user name to clean
 * @returns {string} - Cleaned user name without trailing zeros
 */
export function cleanUserName(name) {
  // Handle null, undefined, empty string, or falsy values
  if (!name) return '';
  
  // Convert to string and trim whitespace
  let cleaned = String(name).trim();

  // If empty after trimming, return empty string
  if (!cleaned) return '';

  // Remove ALL trailing zeros (one or more) using regex - AGGRESSIVE
  // /0+$/ matches one or more zeros at the end of the string
  // Use global flag and do it multiple times to be absolutely sure
  while (cleaned.endsWith('0')) {
    cleaned = cleaned.replace(/0+$/, '');
  }
  
  // Also remove any trailing zeros that might be separated by spaces
  cleaned = cleaned.replace(/\s*0+$/, '');
  
  // Trim again in case removing zeros left trailing spaces
  const result = cleaned.trim();
  
  // Final safety check - if result still ends with 0, remove it
  if (result.endsWith('0')) {
    return result.slice(0, -1).trim();
  }
  
  return result;
}

